import { type RouteRecordRaw } from "vue-router";

import { pb } from "#api/pocketbase";
import { routeNames } from "./constants";

// NOTE: Utilize route code-splitting where appropriate/beneficial (Vue router recommendation)
// component: () => import("../views/AboutView.vue"),

/*
 * There are several types of route protection:
 *   - requiresAuth     - Route is unaccessible when not authenticated
 *   - requiresNoAuth   - Route is unaccessible when authenticated
 */

/** Routes that require the user to be unauthenticated */
const unauthenticatedRoutes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "welcome",
    component: () => import("#views/welcome/WelcomePage.vue"),
    beforeEnter: () => {
      // Redirect authenticated users to songs page
      // NOTE: Cannot require no auth as route as this will cause infinite redirect loop for authenticated users!
      const authenticated = pb.authStore.isValid;
      if (authenticated) {
        return { name: routeNames.song.index };
      }
    },
  },
  {
    path: "/auth",
    name: "authRouter",
    component: () => import("#views/auth/AuthRouter.vue"),
    redirect: "/auth/login",
    children: [
      {
        path: "signup",
        name: routeNames.auth.signup,
        component: () => import("#views/auth/signup/SignupPage.vue"),
        meta: { requiresNoAuth: true },
      },
      {
        path: "login",
        name: routeNames.auth.login,
        component: () => import("#views/auth/login/LoginPage.vue"),
        meta: { requiresNoAuth: true },
      },
      // Logout route should always be accessible regardless of authentication status
      {
        path: "logout",
        name: routeNames.auth.logout,
        component: () => import("#views/auth/logout/index.vue"),
      },
      {
        path: "password/forgot",
        name: "authPasswordForget",
        component: () => import("#views/auth/password-forget/PasswordForgetPage.vue"),
        meta: { requiresNoAuth: true },
      },
      {
        path: "password/reset",
        name: "authPasswordReset",
        component: () => import("#views/auth/password-reset/PasswordResetPage.vue"),
        meta: { requiresNoAuth: true },
      },
      {
        path: "verify",
        name: routeNames.auth.verify,
        component: () => import("#views/auth/verify/VerifyPage.vue"),
        meta: { requiresNoAuth: true },
      },
    ],
  },
];

/** Routes that require the user to be authenticated */
const authenticatedRoutes: RouteRecordRaw[] = [
  {
    path: "/",
    meta: { requiresAuth: true },
    children: [
      // Dashboard route redirects to root route of user type
      {
        path: "dashboard",
        name: routeNames.dashboard,
        component: () => import("#views/dashboard/index/DashboardPage.vue"),
      },
      {
        path: "groups",
        children: [
          {
            path: "",
            name: routeNames.group.index,
            component: () => import("#views/groups/index/GroupsPage.vue"),
          },
          {
            path: "create",
            name: routeNames.group.create,
            component: () => import("#views/groups/form/GroupFormPage.vue"),
          },
          {
            path: ":id",
            name: routeNames.group.detail,
            component: () => import("#views/groups/details/GroupDetailPage.vue"),
          },
          {
            path: ":id/edit",
            name: routeNames.group.edit,
            component: () => import("#views/groups/form/GroupFormPage.vue"),
          },
        ],
      },
      {
        path: "lists",
        children: [
          {
            path: "",
            name: routeNames.list.index,
            component: () => import("#views/lists/index/ListsPage.vue"),
          },
          {
            path: "create",
            name: routeNames.list.create,
            component: () => import("#views/lists/form/ListFormPage.vue"),
          },
          {
            path: ":id",
            name: routeNames.list.detail,
            component: () => import("#views/lists/details/ListDetailPage.vue"),
          },
          {
            path: ":id/edit",
            name: routeNames.list.edit,
            component: () => import("#views/lists/form/ListFormPage.vue"),
          },
        ],
      },
      {
        path: "songs",
        children: [
          {
            path: "",
            name: routeNames.song.index,
            component: () => import("#views/songs/index/SongsPage.vue"),
          },
          {
            path: "create",
            name: routeNames.song.create,
            component: () => import("#views/songs/form/SongFormPage.vue"),
          },
          {
            path: ":id",
            name: routeNames.song.detail,
            component: () => import("#views/songs/details/SongDetailPage.vue"),
          },
          {
            path: ":id/edit",
            name: routeNames.song.edit,
            component: () => import("#views/songs/form/SongFormPage.vue"),
          },
        ],
      },
      {
        path: "account",
        children: [
          {
            path: "profile",
            name: routeNames.auth.profile,
            component: () => import("#views/account/profile/AccountProfilePage.vue"),
          },
        ],
      },
    ],
  },
];

const routes: RouteRecordRaw[] = [
  ...unauthenticatedRoutes,
  ...authenticatedRoutes,
  {
    path: "/:pathMatch(.*)*",
    name: "pageNotFound",
    component: () => import("#views/PageNotFound.vue"),
  },
];

export default routes;
